import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Input,
  Button,
  Typography,
  Avatar,
  Descriptions,
  Alert,
  message,
  Divider,
  Tag,
  Flex,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import PageShell from '../../components/common/PageShell';

const { Title, Text } = Typography;

const ProfilePage = () => {
  const [form] = Form.useForm();
  const { user, updateUser } = useAuth();

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fullName = user
    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
    : '';

  const initials = fullName
    ? fullName.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  const handleEdit = () => {
    form.setFieldsValue({
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      phone: user?.primary_phone ?? user?.extra?.phone ?? '',
    });
    setEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.updateProfile({
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
      });
      const updated = response?.data ?? response;
      updateUser(updated);
      setEditing(false);
      message.success('Profile updated successfully');
    } catch (err) {
      const msg = err?.response?.data?.message ?? 'Failed to save changes. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const portalStatusColor = user?.portal_status === 'active' ? 'success' : 'default';

  return (
    <PageShell>
      <PageHeader
        title="My Profile"
        breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Profile' }]}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8} lg={7}>
          <Card
            style={{ borderRadius: 8, border: '1px solid var(--varexe-border-color)', boxShadow: 'var(--varexe-box-shadow-sm)', textAlign: 'center' }}
          >
            <Avatar
              size={80}
              style={{
                background: 'var(--varexe-soft-primary)',
                color: 'var(--varexe-primary)',
                fontSize: 28,
                fontWeight: 700,
                marginBottom: 12,
              }}
            >
              {initials}
            </Avatar>

            <Title level={4} style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>
              {fullName || '—'}
            </Title>

            {user?.business_name && (
              <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 8 }}>
                {user.business_name}
              </Text>
            )}

            <Tag color={portalStatusColor} style={{ marginBottom: 16 }}>
              {user?.portal_status === 'active' ? 'Portal Active' : user?.portal_status ?? 'Unknown'}
            </Tag>

            <Divider style={{ margin: '12px 0' }} />

            <Descriptions column={1} size="small" colon={false} labelStyle={{ color: 'var(--varexe-gray-500)', fontSize: 12 }}>
              <Descriptions.Item
                label={(
                  <Flex align="center" gap={4}>
                    <MailOutlined />
                    Email
                  </Flex>
                )}
              >
                <Text style={{ fontSize: 13 }}>{user?.portal_email ?? '—'}</Text>
              </Descriptions.Item>
              {(user?.primary_phone || user?.extra?.phone) && (
                <Descriptions.Item
                  label={(
                    <Flex align="center" gap={4}>
                      <PhoneOutlined />
                      Phone
                    </Flex>
                  )}
                >
                  <Text style={{ fontSize: 13 }}>{user?.primary_phone ?? user?.extra?.phone}</Text>
                </Descriptions.Item>
              )}
              {user?.customer_since && (
                <Descriptions.Item
                  label={(
                    <Flex align="center" gap={4}>
                      <CalendarOutlined />
                      Customer Since
                    </Flex>
                  )}
                >
                  <Text style={{ fontSize: 13 }}>{dayjs(user.customer_since).format('MMM YYYY')}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            {(user?.last_login || user?.login_count) && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Flex vertical align="flex-start" gap={4}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <LoginOutlined style={{ marginRight: 4 }} />
                    Login Activity
                  </Text>
                  {user?.last_login && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Last login: {dayjs(user.last_login).format('MMM D, YYYY [at] h:mm A')}
                    </Text>
                  )}
                  {user?.login_count > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Total logins: {user.login_count}
                    </Text>
                  )}
                </Flex>
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} md={16} lg={17}>
          <Card
            title={<Text style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>Personal Information</Text>}
            extra={
              !editing && (
                <Button variant="filled" color="default" icon={<EditOutlined />} onClick={handleEdit} style={{ borderRadius: 6 }}>
                  Edit
                </Button>
              )
            }
            style={{ borderRadius: 8, border: '1px solid var(--varexe-border-color)', boxShadow: 'var(--varexe-box-shadow-sm)' }}
          >
            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 20, borderRadius: 6 }}
              />
            )}

            {editing ? (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                requiredMark={false}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="First Name"
                      name="first_name"
                      rules={[{ required: true, message: 'First name is required' }]}
                    >
                      <Input prefix={<UserOutlined style={{ color: 'var(--varexe-gray-400)' }} />} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Last Name"
                      name="last_name"
                      rules={[{ required: true, message: 'Last name is required' }]}
                    >
                      <Input prefix={<UserOutlined style={{ color: 'var(--varexe-gray-400)' }} />} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="Phone Number" name="phone">
                  <Input prefix={<PhoneOutlined style={{ color: 'var(--varexe-gray-400)' }} />} placeholder="Optional" />
                </Form.Item>

                <Form.Item label="Portal Email">
                  <Input
                    value={user?.portal_email}
                    disabled
                    prefix={<MailOutlined />}
                    suffix={<Tag color="default" style={{ margin: 0 }}>Read-only</Tag>}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    To change your email, please contact your account manager.
                  </Text>
                </Form.Item>

                <Flex gap={8} style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={loading}
                    style={{ borderRadius: 6 }}
                  >
                    Save Changes
                  </Button>
                  <Button variant="filled" color="default" onClick={handleCancel} style={{ borderRadius: 6 }}>
                    Cancel
                  </Button>
                </Flex>
              </Form>
            ) : (
              <Descriptions
                column={{ xs: 1, sm: 2 }}
                size="default"
                colon={false}
                labelStyle={{ color: 'var(--varexe-gray-500)', fontWeight: 500, width: '40%' }}
              >
                <Descriptions.Item label="First Name">
                  <Text>{user?.first_name ?? '—'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Last Name">
                  <Text>{user?.last_name ?? '—'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Portal Email">
                  <Text>{user?.portal_email ?? '—'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  <Text>{user?.primary_phone ?? user?.extra?.phone ?? '—'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Customer Type">
                  <Text style={{ textTransform: 'capitalize' }}>{user?.customer_type ?? '—'}</Text>
                </Descriptions.Item>
                {user?.business_name && (
                  <Descriptions.Item label="Business Name">
                    <Text>{user.business_name}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
          </Card>

          <Card
            style={{ marginTop: 16, borderRadius: 8, border: '1px solid var(--varexe-border-color)', boxShadow: 'var(--varexe-box-shadow-sm)' }}
          >
            <Flex vertical gap={4}>
              <Text strong style={{ fontSize: 13 }}>Password & Security</Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                To change your password, please contact your account manager or request a password reset via the login page.
              </Text>
            </Flex>
          </Card>
        </Col>
      </Row>
    </PageShell>
  );
};

export default ProfilePage;
